import {addDays, toISO} from '../lib/dateUtils.js';
import {BROODER_CLEANOUT, SCHOONER_CLEANOUT, overlaps} from '../lib/broiler.js';

type Allocation = {
  id?: string;
  brooder_name?: string | null;
  brooder_entry_date?: string | null;
  brooder_exit_date?: string | null;
  schooner_name?: string | null;
  schooner_entry_date?: string | null;
  schooner_exit_date?: string | null;
};
type BroilerAllocation = Allocation & {
  brooder?: string | number | null;
  schooner?: string | number | null;
  brooderIn?: string | null;
  brooderOut?: string | null;
  schoonerIn?: string | null;
  schoonerOut?: string | null;
};
const fields = [
  'brooder_name',
  'brooder_entry_date',
  'brooder_exit_date',
  'schooner_name',
  'schooner_entry_date',
  'schooner_exit_date',
] as const;
export const layerAllocationKey = (row: Allocation) => JSON.stringify(fields.map((key) => row[key] || ''));
const numberPart = (name: string | number | null | undefined) =>
  String(name ?? '')
    .trim()
    .replace(/^(brooder|schooner)\s+/i, '');
const plusDays = (date: string, days: number) => toISO(addDays(new Date(date + 'T12:00:00'), days));

/** Same occupancy windows as the existing pickers, with canonical label matching. */
export function layerAllocationConflicts(
  draft: Allocation,
  ownId: string,
  broilers: BroilerAllocation[],
  layers: Allocation[],
) {
  const warnings: string[] = [];
  for (const phase of ['brooder', 'schooner'] as const) {
    const name = draft[`${phase}_name`];
    const entry = draft[`${phase}_entry_date`];
    const days = phase === 'brooder' ? 21 : 119;
    const cleanout = phase === 'brooder' ? BROODER_CLEANOUT : SCHOONER_CLEANOUT;
    const exit =
      phase === 'brooder'
        ? entry && plusDays(entry, days + cleanout)
        : draft.schooner_exit_date || (entry && plusDays(entry, days + cleanout));
    if (!name || !entry || !exit) continue;
    const broilerConflict = broilers.some((row) => {
      if (row.id === ownId || numberPart(row[phase]) !== numberPart(name)) return false;
      const start = row[`${phase}In`] || row[`${phase}_entry_date`] || '';
      const end = plusDays(row[`${phase}Out`] || row[`${phase}_exit_date`] || entry, cleanout);
      return overlaps(entry, exit, start, end);
    });
    const layerConflict = layers.some((row) => {
      const start = row[`${phase}_entry_date`];
      if (row.id === ownId || !start || numberPart(row[`${phase}_name`]) !== numberPart(name)) return false;
      const end = row[`${phase}_exit_date`] || plusDays(start, days + cleanout);
      return overlaps(entry, exit, start, end);
    });
    if (broilerConflict || layerConflict) warnings.push(`${name} is already in use during these dates.`);
  }
  return warnings;
}
